/**
 * App root — routes the v1.1 screens over Deep-Linked State (route.ts).
 *
 * Screens:
 *  - `/` → DirectoryPage (S-DIR, the TPIN-first landing; PortfolioSummary is dormant, PD-34)
 *  - `/deployment/{tinBranch}` → DeploymentDetail (S3 failed list)
 *  - `/deployment/{tinBranch}/pending` → PendingListPage (S4 / FR-4)
 *  - `/tpin/{tin}` and `/tpin/{tin}/branch/{dbName}` → S-DASH / S-BRANCH
 *
 * Wrapped in AuthGate: the dashboard is only mounted when the gateway's
 * GET /api/auth/me confirms an active session (ADR-003 D2). On any 401 from
 * the data stores, AuthStore.markSessionExpired() re-renders the gate and
 * the login page reappears.
 */

import { useEffect } from 'react'
import { DirectoryPage } from '@pages/DirectoryPage'
import { DeploymentDetail } from '@pages/DeploymentDetail'
import { PendingListPage } from '@pages/PendingListPage'
import { TpinScreens, isTpinScreenPath } from '@pages/TpinScreens'
import { AuthGate } from '@pages/AuthGate'
import { useRoute } from '@components/route'
import { APP_BUILD_VERSION } from '@lib/buildVersion'
import './styles.css'

function ProtectedApp(): JSX.Element {
  const route = useRoute()

  useEffect(() => {
    const knownPrefix =
      window.location.pathname === '/' ||
      /^\/deployment\/[0-9]+_[0-9]+(\/pending)?\/?$/.test(window.location.pathname) ||
      isTpinScreenPath(window.location.pathname)
    if (knownPrefix) return
    // Unknown paths land on the home, the TPIN directory (Escape Hatch semantics).
    window.history.replaceState({}, '', '/')
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [])

  return (
    <div className="app">
      {isTpinScreenPath(window.location.pathname) ? (
        <TpinScreens />
      ) : route.deployment !== null ? (
        route.pendingList ? (
          <PendingListPage dbName={route.deployment} />
        ) : (
          <DeploymentDetail dbName={route.deployment} />
        )
      ) : (
        <DirectoryPage />
      )}
      {/* Quiet build footer (proposal §3.4 — Q13a default). The footer
       * sits outside the page-specific <main> so it spans every screen
       * without leaking into individual page tests. The build version
       * comes from the Vite import.meta.env (resolved at build time). */}
      <footer className="app-footer" data-testid="app-footer">
        Smart Invoice Health · build {APP_BUILD_VERSION}
      </footer>
    </div>
  )
}

export function App(): JSX.Element {
  return (
    <AuthGate>
      <ProtectedApp />
    </AuthGate>
  )
}
